import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Wand2 } from "lucide-react";
import { API_BASE, formatApiError } from "../components/AuthContext";

function csv(s) { return (s || "").split(",").map((x) => x.trim()).filter(Boolean); }
function joinCsv(arr) { return (arr || []).join(", "); }

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2 text-[13px] font-mono" />
    </div>
  );
}

function Select({ label, value, onChange, children, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]">
        {children}
      </select>
    </div>
  );
}

function buildPayload(form) {
  return {
    name: form.name,
    enabled: form.enabled,
    priority: Number(form.priority) || 100,
    conditions: {
      tags_any: csv(form.tags_any),
      tags_all: csv(form.tags_all),
      filename_contains: form.filename_contains || null,
      visibility: form.visibility || null,
      source: form.source || null,
    },
    actions: {
      move_to_folder: form.move_to_folder || null,
      add_tags: csv(form.add_tags),
      remove_tags: csv(form.remove_tags),
      set_visibility: form.set_visibility || null,
    },
  };
}

function initialForm(rule) {
  return {
    name: rule?.name || "",
    enabled: rule?.enabled !== false,
    priority: rule?.priority || 100,
    tags_any: joinCsv(rule?.conditions?.tags_any),
    tags_all: joinCsv(rule?.conditions?.tags_all),
    filename_contains: rule?.conditions?.filename_contains || "",
    visibility: rule?.conditions?.visibility || "",
    source: rule?.conditions?.source || "",
    move_to_folder: rule?.actions?.move_to_folder || "",
    add_tags: joinCsv(rule?.actions?.add_tags),
    remove_tags: joinCsv(rule?.actions?.remove_tags),
    set_visibility: rule?.actions?.set_visibility || "",
  };
}

export default function RuleModal({ rule, onClose, onSaved }) {
  const isNew = !rule?.rule_id;
  const [form, setForm] = useState(() => initialForm(rule));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = buildPayload(form);
      if (isNew) await axios.post(`${API_BASE}/document-rules`, payload);
      else await axios.patch(`${API_BASE}/document-rules/${rule.rule_id}`, payload);
      toast.success(isNew ? "Rule created" : "Rule updated");
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="rule-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">{isNew ? "New rule" : "Edit rule"}</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>
              <Wand2 size={20} className="inline mr-2" style={{ color: "var(--accent)" }} />
              Auto-organize
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="rule-modal-close"><X size={20} /></button>
        </div>

        <form onSubmit={save} className="space-y-5" data-testid="rule-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="overline block mb-1.5">Name</label>
              <input value={form.name} required onChange={(e) => upd("name")(e.target.value)} data-testid="rule-name"
                placeholder="e.g. Tax returns → Tax 2026"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Priority</label>
              <input type="number" value={form.priority} onChange={(e) => upd("priority")(e.target.value)} data-testid="rule-priority"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] font-mono" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={form.enabled} onChange={(e) => upd("enabled")(e.target.checked)} data-testid="rule-enabled" />
            Enabled
          </label>

          <RuleConditionsCard form={form} upd={upd} />
          <RuleActionsCard form={form} upd={upd} />

          {err && <div className="text-[13px] text-[var(--accent)]" data-testid="rule-error">{err}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="rule-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Saving…" : (isNew ? "Create" : "Update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RuleConditionsCard({ form, upd }) {
  return (
    <div className="card p-4" style={{ background: "var(--subtle)" }}>
      <div className="overline mb-3">When (conditions)</div>
      <div className="space-y-3">
        <Field label="Tags — any of (csv)" value={form.tags_any} onChange={upd("tags_any")} testid="rule-tags-any" placeholder="tax, return" />
        <Field label="Tags — all of (csv)" value={form.tags_all} onChange={upd("tags_all")} testid="rule-tags-all" />
        <Field label="Filename contains" value={form.filename_contains} onChange={upd("filename_contains")} testid="rule-filename" placeholder="e.g. 1099" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Visibility" value={form.visibility} onChange={upd("visibility")} testid="rule-visibility">
            <option value="">Any</option><option value="firm">Internal</option><option value="client">Shared with client</option>
          </Select>
          <Select label="Source" value={form.source} onChange={upd("source")} testid="rule-source">
            <option value="">Any</option><option value="firm">Uploaded by firm</option><option value="client">Uploaded by client</option>
          </Select>
        </div>
      </div>
    </div>
  );
}

function RuleActionsCard({ form, upd }) {
  return (
    <div className="card p-4" style={{ background: "var(--subtle)" }}>
      <div className="overline mb-3">Then (actions)</div>
      <div className="space-y-3">
        <Field label="Move to folder" value={form.move_to_folder} onChange={upd("move_to_folder")} testid="rule-move-folder" placeholder="e.g. Tax 2026" />
        <Field label="Add tags (csv)" value={form.add_tags} onChange={upd("add_tags")} testid="rule-add-tags" placeholder="sorted, processed" />
        <Field label="Remove tags (csv)" value={form.remove_tags} onChange={upd("remove_tags")} testid="rule-remove-tags" />
        <Select label="Set visibility" value={form.set_visibility} onChange={upd("set_visibility")} testid="rule-set-visibility">
          <option value="">Don't change</option><option value="firm">Internal</option><option value="client">Shared with client</option>
        </Select>
      </div>
    </div>
  );
}
